import {
  DEAL_OTC_DERIVATIVE_QUERY_PARAM,
  getOtcDerivativeKind,
  getUrlProductType,
} from '@src/components/DealProductBuilder/productConfig';
import {
  LEGACY_ANALYTICS_BASE_PATH,
  TRADING_ANALYTICS_BASE_PATH,
  type AnalyticsBasePath,
} from '@src/tokens/navigationPaths';
import type { DealBookingType } from '@src/types/OTC';
import type { LedgerAccountTypeEnum } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { generatePath, matchPath, type PathMatch } from 'react-router-dom';
import type { MultilegComboType } from '../../components/MultilegCombo/enums';
import type { PreTradeTabType } from '../../components/PreTrade/types';
import type { LayoutSection } from '../../types/LayoutConfig';
import type { OrderDetailsPath, OrderRouteType } from '../Trading/Markets/OrderDetails/types';
import type { ExtractRouteParams } from './routes.types';
import { EMPTY_SYMBOL_PLACEHOLDER } from './tokens';

/** `/:section/:layoutId/...` or `/:section/:navGroupName/:layoutId/...` when `navGroupName` is set. */
export function getSpaceSectionPathPrefix(
  section: 'trading' | 'monitoring',
  layoutId: string,
  navGroupName?: string | null
): string {
  if (navGroupName != null && navGroupName !== '') {
    return `/${section}/${navGroupName}/${layoutId}`;
  }
  return `/${section}/${layoutId}`;
}

/**
 * Match a trading URL after the space root: ungrouped and grouped shapes share the same tail
 * (e.g. `symbol/:symbol/:id?`).
 */
export function matchTradingPathname(pathname: string, subpathAfterLayoutId: string): PathMatch<string> | null {
  const grouped = `/trading/:navGroupName/:layoutId/${subpathAfterLayoutId}`;
  const ungrouped = `/trading/:layoutId/${subpathAfterLayoutId}`;
  // Prefer ungrouped first so `/trading/:layoutId/order/...` is not parsed as grouped with `layoutId=order`.
  return matchPath({ path: ungrouped, end: false }, pathname) ?? matchPath({ path: grouped, end: false }, pathname);
}

/** Same as {@link matchTradingPathname} for `/monitoring/...` space URLs. */
export function matchMonitoringSpacePathname(pathname: string, subpathAfterLayoutId: string): PathMatch<string> | null {
  const grouped = `/monitoring/:navGroupName/:layoutId/${subpathAfterLayoutId}`;
  const ungrouped = `/monitoring/:layoutId/${subpathAfterLayoutId}`;
  return matchPath({ path: ungrouped, end: false }, pathname) ?? matchPath({ path: grouped, end: false }, pathname);
}

const DeepDiveTailPath = '/symbol/:symbol/:id?';
const DeepDiveRoutePath = '/trading/:navGroupName?/:layoutId/symbol/:symbol/:id?';
export type DeepDiveRouteParams = ExtractRouteParams<typeof DeepDiveRoutePath>;
export const getDeepDiveRoute = (
  params?: DeepDiveRouteParams & {
    marketAccountNames?: string[];
    marketNames?: string[];
    aggregation?: string;
  }
): string => {
  if (params) {
    const {
      symbol,
      id = null,
      marketAccountNames = [],
      marketNames = [],
      aggregation = null,
      layoutId = null,
      navGroupName,
    } = params;
    const urlArgsArray = compact([
      marketAccountNames && marketAccountNames.length > 0 && `marketAccountNames=${marketAccountNames.join(',')}`,
      marketNames && marketNames.length > 0 && `marketNames=${marketNames.join(',')}`,
      aggregation && `aggregation=${aggregation}`,
    ]);

    const urlArgs = urlArgsArray.length > 0 ? `?${urlArgsArray.join('&')}` : '';
    const root = getSpaceSectionPathPrefix('trading', layoutId!, navGroupName);
    const tail = generatePath(DeepDiveTailPath, {
      symbol,
      id,
    });
    return `${root}${tail}${urlArgs}`;
  } else {
    return DeepDiveRoutePath;
  }
};

const MultilegDeepDiveTailPath = '/multileg/:instrumentType/:symbol?/:id?';
const MultilegDeepDiveRoutePath = '/trading/:navGroupName?/:layoutId/multileg/:instrumentType/:symbol?/:id?';
type InnerMultilegDeepDiveParams = ExtractRouteParams<typeof MultilegDeepDiveRoutePath>;
export interface MultilegDeepDiveParams extends Omit<InnerMultilegDeepDiveParams, 'instrumentType'> {
  instrumentType: MultilegComboType;
  marketAccountNames?: string[];
  marketNames?: string[];
  legSymbols?: string[];
  isClone?: boolean;
  isEdit?: boolean;
}
export const getMultilegDeepDiveRoute = (params?: MultilegDeepDiveParams): string => {
  if (params) {
    const {
      instrumentType,
      symbol = null,
      id = null,
      layoutId = null,
      navGroupName,
      marketAccountNames = [],
      marketNames = [],
      legSymbols = [],
      isClone = false,
      isEdit = false,
    } = params;
    const urlArgsArray = compact([
      marketAccountNames && marketAccountNames.length > 0 && `marketAccountNames=${marketAccountNames.join(',')}`,
      marketNames && marketNames.length > 0 && `marketNames=${marketNames.join(',')}`,
      legSymbols && legSymbols.length > 0 && `legSymbols=${legSymbols.join(',')}`,
      isClone && 'clone=true',
      isEdit && 'edit=true',
    ]);
    const urlArgs = urlArgsArray.length > 0 ? `?${urlArgsArray.join('&')}` : '';

    // When id is present but symbol is not, use placeholder so id goes in the correct path segment.
    // Otherwise generatePath would put id in the symbol position (/:symbol?/:id?).
    // Pass raw symbol — RR7 generatePath encodes path params (do not pre-encode).
    const symbolParam = symbol != null && symbol !== '' ? symbol : id != null ? EMPTY_SYMBOL_PLACEHOLDER : null;
    const root = getSpaceSectionPathPrefix('trading', layoutId!, navGroupName);
    const tail = generatePath(MultilegDeepDiveTailPath, {
      instrumentType,
      symbol: symbolParam,
      id,
    });
    return `${root}${tail}${urlArgs}`;
  } else {
    return MultilegDeepDiveRoutePath;
  }
};

const DealProductBuilderTailPath = '/deals/:bookingType/:symbol?/:id?';
const DealProductBuilderRoutePath = '/trading/:navGroupName?/:layoutId/deals/:bookingType/:symbol?/:id?';
export const getDealProductBuilderRoute = (params?: {
  bookingType: DealBookingType;
  symbol?: string;
  id?: string;
  layoutId: string;
  navGroupName?: string;
}) => {
  if (params) {
    // Use consolidated URL product type (e.g., 'loan' instead of 'fixedRateLoan'/'floatingRateLoan')
    const urlBookingType = getUrlProductType(params.bookingType);
    const root = getSpaceSectionPathPrefix('trading', params.layoutId, params.navGroupName);
    const basePath = generatePath(DealProductBuilderTailPath, {
      bookingType: urlBookingType,
      symbol: params.symbol ? params.symbol : null,
      id: params.id ?? null,
    });
    const path = `${root}${basePath}`;
    if (params.bookingType === 'fixedRateLoan' || params.bookingType === 'floatingRateLoan') {
      return `${path}?${DEAL_OTC_DERIVATIVE_QUERY_PARAM}=${params.bookingType}`;
    }
    if (params.bookingType === 'forward' || params.bookingType === 'option') {
      const otcKind = getOtcDerivativeKind(params.bookingType);
      return `${path}?${DEAL_OTC_DERIVATIVE_QUERY_PARAM}=${otcKind}`;
    }
    return path;
  } else {
    return DealProductBuilderRoutePath;
  }
};

const ReconDetailsTailPath = '/recon-mismatches/details/:id?';
const ReconDetailsRoutePath = '/trading/:navGroupName?/:layoutId/recon-mismatches/details/:id?';
export type ReconDetailsRouteParams = ExtractRouteParams<typeof ReconDetailsRoutePath>;
export const getReconDetailsRoute = (
  params?: ReconDetailsRouteParams & { mismatchIDs: string[]; layoutId?: string; navGroupName?: string }
) => {
  if (params) {
    const root = getSpaceSectionPathPrefix('trading', params.layoutId!, params.navGroupName);
    return `${root}${generatePath(ReconDetailsTailPath, { id: params.id ?? null })}?${
      params.mismatchIDs ? `mismatchIDs=${params.mismatchIDs.join(',')}` : ''
    }`;
  } else {
    return ReconDetailsRoutePath;
  }
};

const OptionsTailPath = '/options/:underlyingCodeOrCurrency/:market/:marketAccount/:id?';
const OptionsRoutePath =
  '/trading/:navGroupName?/:layoutId/options/:underlyingCodeOrCurrency/:market/:marketAccount/:id?';
export type OptionsRouteParams = ExtractRouteParams<typeof OptionsRoutePath>;
export const getOptionsRoute = (
  params?: OptionsRouteParams & {
    expiry?: string;
  }
) => {
  const searchParams = new URLSearchParams(
    compact([params?.expiry ? ['expiry', params?.expiry] : undefined])
  ).toString();
  if (params) {
    const root = getSpaceSectionPathPrefix('trading', params.layoutId!, params.navGroupName);
    return `${root}${generatePath(OptionsTailPath, {
      underlyingCodeOrCurrency: params.underlyingCodeOrCurrency ?? null,
      market: params.market ?? null,
      marketAccount: params.marketAccount ?? null,
      id: params.id ?? null,
    })}${searchParams ? `?${searchParams}` : ''}`;
  } else {
    return OptionsRoutePath;
  }
};

const AccountLedgerEventsDetailsTailPath = '/account-ledger-events/:id?';
const AccountLedgerEventsDetailsRoutePath = '/trading/:navGroupName?/:layoutId/account-ledger-events/:id?';
export type AccountLedgerEventsDetailsRouteParams = ExtractRouteParams<typeof AccountLedgerEventsDetailsRoutePath>;
export const getAccountLedgerEventsDetailsRoute = (
  params?: AccountLedgerEventsDetailsRouteParams & {
    asset?: string;
    account: string;
    accountType: LedgerAccountTypeEnum;
  }
) => {
  if (params) {
    const root = getSpaceSectionPathPrefix('trading', params.layoutId, params.navGroupName);
    return `${root}${generatePath(AccountLedgerEventsDetailsTailPath, {
      id: params.id ?? null,
    })}?accountType=${params.accountType}&account=${params.account}&asset=${params.asset ?? ''}`;
  } else {
    return AccountLedgerEventsDetailsRoutePath;
  }
};

function buildTradingSpaceRoute(
  params:
    | {
        layoutId: string;
        navGroupName?: string;
      }
    | undefined,
  tailPath: string,
  tailParams: Record<string, string | null | undefined>,
  fallbackPath: string
) {
  if (!params) {
    return fallbackPath;
  }
  const root = getSpaceSectionPathPrefix('trading', params.layoutId, params.navGroupName);
  return `${root}${generatePath(tailPath, tailParams)}`;
}

const PositionAutoHedgingRulesTailPath = '/position-auto-hedging-rules/:id?';
const PositionAutoHedgingRulesRoutePath = '/trading/:navGroupName?/:layoutId/position-auto-hedging-rules/:id?';
export type PositionAutoHedgingRulesRouteParams = ExtractRouteParams<typeof PositionAutoHedgingRulesRoutePath>;
export const getPositionAutoHedgingRulesRoute = (params?: PositionAutoHedgingRulesRouteParams) =>
  buildTradingSpaceRoute(
    params,
    PositionAutoHedgingRulesTailPath,
    { id: params?.id ?? null },
    PositionAutoHedgingRulesRoutePath
  );

const ManualRFQsTailPath = '/manual-rfqs/:id?';
const ManualRFQsRoutePath = '/trading/:navGroupName?/:layoutId/manual-rfqs/:id?';
export type ManualRFQsRouteParams = ExtractRouteParams<typeof ManualRFQsRoutePath>;
export const getManualRFQsRoute = (params?: ManualRFQsRouteParams) =>
  buildTradingSpaceRoute(params, ManualRFQsTailPath, { id: params?.id ?? null }, ManualRFQsRoutePath);

const ManualRFQPricingTailPath = '/manual-rfq-pricing/:rfqID?';
const ManualRFQPricingRoutePath = '/trading/:navGroupName?/:layoutId/manual-rfq-pricing/:rfqID?';
export type ManualRFQPricingRouteParams = ExtractRouteParams<typeof ManualRFQPricingRoutePath>;
export const getManualRFQPricingRoute = (params?: ManualRFQPricingRouteParams) =>
  buildTradingSpaceRoute(params, ManualRFQPricingTailPath, { rfqID: params?.rfqID ?? null }, ManualRFQPricingRoutePath);

const RFQSweepingTailPath = '/rfq-sweeping/:sweepGroupID?';
const RFQSweepingRoutePath = '/trading/:navGroupName?/:layoutId/rfq-sweeping/:sweepGroupID?';
export type RFQSweepingRouteParams = ExtractRouteParams<typeof RFQSweepingRoutePath>;
export const getRFQSweepingRoute = (params?: RFQSweepingRouteParams) =>
  buildTradingSpaceRoute(
    params,
    RFQSweepingTailPath,
    { sweepGroupID: params?.sweepGroupID ?? null },
    RFQSweepingRoutePath
  );

const TakerQuoteBreakdownTailPath = '/quote-breakdown/:rfqID?';
const TakerQuoteBreakdownRoutePath = '/trading/:navGroupName?/:layoutId/quote-breakdown/:rfqID?';
export type TakerQuoteBreakdownRouteParams = ExtractRouteParams<typeof TakerQuoteBreakdownRoutePath>;
export const getTakerQuoteBreakdownRoute = (params?: TakerQuoteBreakdownRouteParams) =>
  buildTradingSpaceRoute(
    params,
    TakerQuoteBreakdownTailPath,
    { rfqID: params?.rfqID ?? null },
    TakerQuoteBreakdownRoutePath
  );

const DealCashflowsTailPath = '/deal/:dealID/cashflows/:id?';
const DealCashflowsRoutePath = '/trading/:navGroupName?/:layoutId/deal/:dealID/cashflows/:id?';
export type DealCashflowsRouteParams = ExtractRouteParams<typeof DealCashflowsRoutePath>;
export const getDealCashflowsRoute = (params?: DealCashflowsRouteParams) =>
  buildTradingSpaceRoute(
    params,
    DealCashflowsTailPath,
    { dealID: params?.dealID ?? null, id: params?.id ?? null },
    DealCashflowsRoutePath
  );

const PreTradeTailPath = '/pre-trade/:sessionId/:id?/:tab?';
const PreTradeRoutePath = '/trading/:navGroupName?/:layoutId/pre-trade/:sessionId/:id?/:tab?';
export type PreTradeRouteParams = ExtractRouteParams<typeof PreTradeRoutePath>;
export function parsePreTradeRouteTab(rawTab: string | undefined): PreTradeTabType | undefined {
  if (rawTab === 'history' || rawTab === 'scenarios' || rawTab === 'details') {
    return rawTab;
  }
  return undefined;
}

export const getPreTradeRoute = (params?: PreTradeRouteParams & { requestId?: string }) => {
  if (params) {
    const root = getSpaceSectionPathPrefix('trading', params.layoutId!, params.navGroupName);
    const path = `${root}${generatePath(PreTradeTailPath, {
      sessionId: params.sessionId ?? null,
      id: params.id ?? null,
      tab: params.tab ?? null,
    })}`;

    if (params.tab === 'details' && params.requestId) {
      return `${path}?requestId=${encodeURIComponent(params.requestId)}`;
    }

    return path;
  } else {
    return PreTradeRoutePath;
  }
};

export const getTradingOrderDetailsRoute = getOrderDetailsRouteFn('trading', true);
export const getMonitoringOrderDetailsRoute = getOrderDetailsRouteFn('monitoring', false);
export const getDealerMonitoringOrderDetailsRoute = getOrderDetailsRouteFn('dealerMonitoring', false);
export const getAnalyticsOrderDetailsRoute = (
  params?: { orderID: string; tab?: OrderDetailsPath },
  basePath: AnalyticsBasePath = LEGACY_ANALYTICS_BASE_PATH
) => generatePath(`${basePath}/order/:orderID${params?.tab == null ? '' : `/${params.tab}`}`, params);

export function rewriteAnalyticsPath(
  pathname: string,
  fromBasePath: AnalyticsBasePath,
  toBasePath: AnalyticsBasePath
): string | null {
  if (pathname === fromBasePath || pathname.startsWith(`${fromBasePath}/`)) {
    return `${toBasePath}${pathname.slice(fromBasePath.length)}`;
  }
  return null;
}

export function getCanonicalAnalyticsPath(pathname: string, basePath: AnalyticsBasePath): string {
  const nonCanonicalBasePath =
    basePath === TRADING_ANALYTICS_BASE_PATH ? LEGACY_ANALYTICS_BASE_PATH : TRADING_ANALYTICS_BASE_PATH;
  return rewriteAnalyticsPath(pathname, nonCanonicalBasePath, basePath) ?? pathname;
}

function matchAnalyticsBasePath(pathname: string, subpath: string): PathMatch<string> | null {
  return (
    matchPath({ path: `${TRADING_ANALYTICS_BASE_PATH}/${subpath}`, end: false }, pathname) ??
    matchPath({ path: `${LEGACY_ANALYTICS_BASE_PATH}/${subpath}`, end: false }, pathname)
  );
}

const OrderDetailsRoutePath = `/:type/:orderID/:tab?`;
type InnerOrderDetailsRouteParams = ExtractRouteParams<typeof OrderDetailsRoutePath>;
export type OrderDetailsRouteParams = InnerOrderDetailsRouteParams &
  Partial<Record<keyof Pick<InnerOrderDetailsRouteParams, 'tab'>, OrderDetailsPath>> &
  Record<keyof Pick<InnerOrderDetailsRouteParams, 'type'>, OrderRouteType> & {
    layoutId?: string;
    navGroupName?: string;
  };

export function getOrderDetailsRouteFn<T extends LayoutSection | 'dealerMonitoring'>(
  context: T,
  includeLayoutId: boolean
): (params?: OrderDetailsRouteParams) => string {
  const base = getBaseRoute(context);
  return params => {
    if (params) {
      if (includeLayoutId && (context === 'trading' || context === 'monitoring')) {
        const layoutId = params.layoutId;
        if (layoutId != null && layoutId !== '') {
          const prefix = getSpaceSectionPathPrefix(context, layoutId, params.navGroupName);
          return `${prefix}${generatePath(OrderDetailsRoutePath, {
            type: params.type,
            orderID: params.orderID,
            tab: params.tab ?? null,
          })}`;
        }
      }
      const path = `${base}${includeLayoutId ? '/:layoutId' : ''}${OrderDetailsRoutePath}`;
      return generatePath(path, params);
    }
    if (includeLayoutId && (context === 'trading' || context === 'monitoring')) {
      return `${base}/:navGroupName?/:layoutId${OrderDetailsRoutePath}`;
    }
    const path = `${base}${includeLayoutId ? '/:layoutId' : ''}${OrderDetailsRoutePath}`;
    return path;
  };
}

function getBaseRoute(context: LayoutSection | 'dealerMonitoring') {
  if (context === 'monitoring') {
    return '/monitoring';
  }
  if (context === 'intel') {
    return '/intel';
  }
  if (context === 'dealer') {
    return '/dealer';
  }
  if (context === 'dealerMonitoring') {
    return '/dealer/monitoring';
  }
  return `/trading`;
}

export const getMarketRoute = (id?: string) => `/trading/${id ? id : ''}`;

export function getOpenTabFromMonitoringRoute(pathname: string) {
  // ['', 'monitoring', 'blotters', :id, :tab]
  const [, , , , openTab] = pathname.split('/');
  return openTab;
}

export function getIdFromMonitoringRoute(pathname: string) {
  // ['', 'monitoring', 'blotters', :id]
  const [, , , id] = pathname.split('/');
  return id;
}

export function getIdFromAnalyticsRoute(pathname: string) {
  const orderMatch = matchAnalyticsBasePath(pathname, 'order/:orderID');
  if (orderMatch?.params.orderID) {
    return orderMatch.params.orderID;
  }
  const tabMatch = matchAnalyticsBasePath(pathname, ':tabId');
  return tabMatch?.params.tabId;
}

export const isCurrentRouteOrderDetails = (pathname: string) =>
  matchTradingPathname(pathname, 'order/*') !== null || matchTradingPathname(pathname, 'customer-order/*') !== null;
export const isCurrentRouteCustomerOrderDetails = (pathname: string) => pathname.includes('/customer-order/');
export const isCurrentRouteDeepDive = (pathname: string) => matchTradingPathname(pathname, 'symbol/*') !== null;
export const isCurrentRouteReconDetails = (pathname: string) =>
  matchTradingPathname(pathname, 'recon-mismatches/details/*') !== null;
export const isCurrentRouteAccountLedgerEventsDetails = (pathname: string) =>
  matchTradingPathname(pathname, 'account-ledger-events/*') !== null;
export const isCurrentRouteDealProductBuilder = (pathname: string) =>
  matchTradingPathname(pathname, 'deals/*') !== null;
export const isCurrentRouteMarket = (pathname: string) =>
  matchPath('/trading/*', pathname) !== null &&
  !isCurrentRouteOrderDetails(pathname) &&
  !isCurrentRouteDeepDive(pathname);
export const isCurrentRouteSettings = (pathname: string) => matchPath('/settings/*', pathname) !== null;
export const isCurrentRouteAdmin = (pathname: string) => matchPath('/admin/*', pathname) !== null;
export const isCurrentRouteTrading = (pathname: string) =>
  !isCurrentRouteAnalytics(pathname) &&
  !isCurrentRouteAnalyticsOrderDetails(pathname) &&
  (matchPath('/trading/:layoutId/*', pathname) !== null ||
    matchPath('/trading/:navGroupName/:layoutId/*', pathname) !== null);
export const isCurrentRouteTalosAdmin = (pathname: string) => matchPath('/talos-admin/*', pathname) !== null;
export const isCurrentRoutePortfolioEngineering = (pathname: string) =>
  matchPath('/portfolio-engineering/*', pathname) !== null;
export const isCurrentRouteResearch = (pathname: string) => matchPath('/research/*', pathname) !== null;
export const isCurrentRouteOperations = (pathname: string) => matchPath('/operations/*', pathname) !== null;

export function isCurrentRouteMonitoring(pathname: string) {
  return matchPath('/monitoring/*', pathname) !== null;
}

export function isCurrentRouteIntel(pathname: string) {
  return matchPath('/intel/*', pathname) !== null;
}

export function isCurrentRouteMonitoringBlotter(pathname: string) {
  return matchPath('/monitoring/blotters/*', pathname) !== null;
}

export const isCurrentRouteDealerMonitoringOrderDetails = (pathname: string) =>
  matchPath('/dealer/monitoring/order/*', pathname) !== null ||
  matchPath('/dealer/monitoring/customer-order/*', pathname) !== null;
export const isCurrentRouteMonitoringOrderDetails = (pathname: string) =>
  matchMonitoringSpacePathname(pathname, 'order/*') !== null ||
  matchMonitoringSpacePathname(pathname, 'customer-order/*') !== null ||
  matchPath('/monitoring/order/*', pathname) !== null ||
  matchPath('/monitoring/customer-order/*', pathname) !== null;

export function isCurrentRouteAnalytics(pathname: string) {
  const matchesBase =
    matchPath({ path: `${TRADING_ANALYTICS_BASE_PATH}/*`, end: false }, pathname) != null ||
    matchPath({ path: `${LEGACY_ANALYTICS_BASE_PATH}/*`, end: false }, pathname) != null;
  return matchesBase && !isCurrentRouteAnalyticsOrderDetails(pathname);
}

export const isCurrentRouteAnalyticsOrderDetails = (pathname: string) =>
  matchAnalyticsBasePath(pathname, 'order/*') != null;

export function isCurrentRouteDealer(pathname: string) {
  return matchPath('/dealer/*', pathname) !== null;
}

/**
 * All trading route paths that follow the pattern /trading/:layoutId/{subPath}/...
 * Used to derive legacy paths for backward compatibility redirects.
 */
const TRADING_ROUTE_PATHS = [
  DeepDiveRoutePath,
  MultilegDeepDiveRoutePath,
  DealProductBuilderRoutePath,
  ReconDetailsRoutePath,
  OptionsRoutePath,
  AccountLedgerEventsDetailsRoutePath,
  PositionAutoHedgingRulesRoutePath,
  ManualRFQsRoutePath,
  ManualRFQPricingRoutePath,
  RFQSweepingRoutePath,
  TakerQuoteBreakdownRoutePath,
] as const;

/**
 * Extracts the first segment after /trading/:layoutId/ from a route path.
 * e.g., '/trading/:layoutId/manual-rfqs/:id?' -> 'manual-rfqs'
 */
export function extractLegacySubPath(routePath: string): string | null {
  const prefixes = ['/trading/:navGroupName/:layoutId/', '/trading/:navGroupName?/:layoutId/', '/trading/:layoutId/'];
  for (const prefix of prefixes) {
    if (routePath.startsWith(prefix)) {
      const rest = routePath.slice(prefix.length);
      return rest.split('/')[0] || null;
    }
  }
  return null;
}

/**
 * Legacy sub-paths derived from TRADING_ROUTE_PATHS.
 * These are paths that existed before the :layoutId segment was added.
 * Used to redirect legacy URLs like /trading/manual-rfqs to /trading/{layoutId}/manual-rfqs
 */
export const LEGACY_TRADING_SUB_PATHS = [
  ...new Set(TRADING_ROUTE_PATHS.map(extractLegacySubPath).filter((p): p is string => p !== null)),
  // Order details routes use a different pattern via getOrderDetailsRouteFn
  'order',
  'customer-order',
] as const;
